import React, { useEffect, useState } from 'react';
import { Col, Row, Tag } from 'antd';
import { makeStyles, MenuItem } from '@material-ui/core';
import { useCreateUser } from 'src/reactQuery/users/mutations/useCreateUser';
import { useDeleteUser } from 'src/reactQuery/users/mutations/useDeleteUser';
import { useUpdateUser } from 'src/reactQuery/users/mutations/useUpdateUser';
import { useFetchUsers } from 'src/reactQuery/users/queries/useFetchUsers';
import { useProject } from 'src/reactQuery/projects/queries/useProject';
import { useGetGeneratedPassword } from 'src/reactQuery/users/queries/useGetGeneratedPassword';
import InnerHeader from '../../component/InnerHeader';
import NoxTable from '../../component/Table';
import { CStandardInput } from '../../component/Input';
import Modal from '../../component/Modal/Modal';
import CButton from '../../component/Button';
import Garbage from '../../asset/garbage.svg';
import Eye from '../../asset/hide.svg';
import OpenEye from '../../asset/eye-open.svg';
import copyIcon from '../../asset/copy.svg';
import formatDate from '../../helpers/formatDate';
import { DefaultSelect } from '../../component/Select';

const useStyles = makeStyles({
  garbage: {
    cursor: 'pointer',
    left: '24px',
    position: 'absolute'
  },
  passwordWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  removeButton: {
    alignItems: 'center',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    marginTop: '12px',
    padding: '0px 6px'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  tagItemWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: '6px',
    justifyContent: 'space-evenly'
  },
  tagItemWrapperAdd: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-evenly'
  },
  tagsItem: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    justifyContent: 'space-evenly'
  }
});

const Users = () => {
  const [showModal, setShowModal] = useState(false);
  const [edit, setEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [id, setId] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [newTagKey, setNewTagKey] = useState('');
  const [tagsDraft, setTagsDraft] = useState();

  const classes = useStyles();

  const { data: users, refetch: refetchUsers } = useFetchUsers();
  const { data: generatedPassword, refetch: refetchGeneratedPassword } = useGetGeneratedPassword();
  const { data: currentProject } = useProject();
  const createUserMutation = useCreateUser({
    onSuccess: () => refetchUsers()
  });
  const deleteUserMutation = useDeleteUser({
    onSuccess: () => refetchUsers()
  });
  const updateUserMutation = useUpdateUser({
    onSuccess: () => refetchUsers()
  });

  const onObjectValueChange = (value, path) => {
    setTagsDraft({
      ...tagsDraft,
      [path]: value
    });
  };

  const onObjectKeyAdd = () => {
    setTagsDraft({
      ...tagsDraft,
      [newTagKey]: ''
    });

    setNewTagKey('');
  };
  const removeKey = (key) => {
    if (!tagsDraft) return;
    const tempDraft = { ...tagsDraft };
    delete tempDraft[key];
    setTagsDraft(tempDraft);
  };

  const showModalHandler = () => {
    setEdit(false);
    setShowModal(true);
    refetchGeneratedPassword();
  };

  const onEdit = (data) => {
    setFullName(data.fullName);
    setEmail(data.email);
    setPassword(data.password);
    setRole(data.role);
    setId(data._id);
    setTagsDraft(data.tags);
  };

  const clearInput = () => {
    setFullName('');
    setEmail('');
    setPassword('');
    setRole('role');
    setShowPassword(false);
    setTagsDraft({});
  };

  const onCancel = () => {
    setShowModal(false);
    setEdit(false);
    clearInput();
  };

  const deleteUser = () => {
    deleteUserMutation.mutate(id);
    clearInput();
    setShowModal(false);
    setConfirmDelete(false);
  };

  const onSave = () => {
    const mutateVariables = {
      email,
      fullName,
      password,
      role,
      tags: tagsDraft
    };
    createUserMutation.mutate(mutateVariables);
    clearInput();
    setShowModal(false);
  };

  const onUpdate = () => {
    const mutateVariables = {
      email,
      fullName,
      id,
      password,
      role,
      tags: tagsDraft
    };
    updateUserMutation.mutate(mutateVariables);
    clearInput();
    setShowModal(false);
  };

  const mapToTable = (users) => {
    const data = [];

    if (!users || users.length === 0) {
      return data;
    }

    users.forEach(({ _id, name, surname, role, createdAt, created, email, tags }, index) => {
      const date = formatDate(createdAt || created);
      const fullName = name?.concat(' ', surname);

      data.push({
        _id,
        addedAt: date,
        addedBy: 'me',
        email,
        fullName,
        key: index,
        role,
        tags
      });
    });

    return data;
  };

  useEffect(() => {
    async function load() {
      refetchUsers();
    }

    if (currentProject) {
      load();
    }
  }, [currentProject]);

  useEffect(() => {
    setPassword(generatedPassword);
  }, [generatedPassword]);

  const data = mapToTable(users);

  const columns = [
    {
      dataIndex: 'fullName',
      key: 'fullName',
      sorter: (a, b) => a.fullName.localeCompare(b.fullName),
      title: <div>Full Name</div>
    },
    {
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.toString().localeCompare(b.email),
      title: 'Email'
    },
    {
      dataIndex: 'addedAt',
      key: 'addedAt',
      sorter: (a, b) => new Date(a.addedAt) - new Date(b.addedAt),
      title: 'Added At'
    },
    {
      dataIndex: 'addedBy',
      key: 'addedBy',
      sorter: (a, b) => a.addedBy - b.addedBy,
      title: 'Added By'
    },
    {
      dataIndex: 'role',
      key: 'role',
      sorter: (a, b) => a.role - b.role,
      title: 'Role'
    },
    {
      key: 'edit',
      render: (data) => {
        return (
          <Tag
            onClick={() => {
              setShowModal(true);
              setEdit(true);
              onEdit(data);
              setPassword('******');
            }}
          >
            edit
          </Tag>
        );
      }
    }
  ];

  const actions = [
    {
      onClick: showModalHandler,
      title: 'Add New User',
      type: 'primary'
    }
  ];

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle="2" title="Users" />
      <NoxTable
        actions={actions}
        checkbox={false}
        columns={columns}
        data={data}
        searchBy="fullName"
      />
      {confirmDelete ? (
        <Modal
          footerActions={
            <>
              <CButton
                onClick={() => setConfirmDelete(false)}
                type="secondary"
                xstyle={{ height: '40px' }}
              >
                Cancel
              </CButton>
              <CButton onClick={deleteUser} type="primary" xstyle={{ height: '40px' }}>
                Delete
              </CButton>
            </>
          }
          handleClose={() => {
            onCancel();
            setConfirmDelete(false);
          }}
          isModalOpen={showModal}
          styles={{
            '& .MuiDialogContent-root': {
              paddingTop: '0px'
            },
            width: '400px'
          }}
          titleContent={<h3 className="modal-title">Delete User</h3>}
        >
          <h3>
            Are you sure you want to delete user <strong>{fullName}</strong> ? You can’t undo this
            action
          </h3>
        </Modal>
      ) : (
        <Modal
          footerActions={
            <>
              {edit && (
                <img
                  alt="delete user button"
                  className={classes.garbage}
                  onClick={() => setConfirmDelete(true)}
                  src={Garbage}
                />
              )}
              <CButton onClick={onCancel} type="secondary" xstyle={{ height: '40px' }}>
                Cancel
              </CButton>
              <CButton
                onClick={edit ? onUpdate : onSave}
                type="primary"
                xstyle={{ height: '40px' }}
              >
                {edit ? 'Edit' : 'Add'}
              </CButton>
            </>
          }
          handleClose={onCancel}
          isModalOpen={showModal}
          styles={{
            '& .MuiDialogContent-root': {
              display: 'flex',
              flexDirection: 'column',
              height: '340px',
              justifyContent: 'space-evenly',
              overflowY: 'scroll',
              paddingTop: '0px'
            },
            width: '400px'
          }}
          titleContent={<h3 className="modal-title">{edit ? 'Edit User' : 'Create User'}</h3>}
        >
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-user-full-name"
            id="standard-input-user-full-name"
            label="Full Name"
            onChange={(e) => setFullName(e.target.value)}
            size="large"
            value={fullName}
            width="true"
          />
          <CStandardInput
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-user-email"
            id="standard-input-user-email"
            label="Email"
            onChange={(e) => setEmail(e.target.value)}
            size="large"
            value={email}
            width="true"
          />
          <div className={classes.passwordWrapper}>
            <CStandardInput
              adornment={
                !edit ? (
                  <>
                    <img
                      alt="copy password icon"
                      onClick={() => {
                        navigator.clipboard.writeText(password);
                      }}
                      src={copyIcon}
                      style={{
                        cursor: 'pointer',
                        marginRight: '5px',
                        width: '18px'
                      }}
                    />
                    <img
                      alt="hide password icon"
                      onClick={() => {
                        setShowPassword((s) => !s);
                      }}
                      src={showPassword ? OpenEye : Eye}
                      style={{ width: '18px' }}
                    />
                  </>
                ) : null
              }
              disabled={!!edit}
              disableUnderline
              formclasses={{ root: classes.root }}
              htmlFor="standard-input-user-password"
              id="standard-input-user-password"
              label="Password"
              onChange={(e) => setPassword(e.target.value)}
              size="large"
              type={showPassword ? 'text' : 'password'}
              value={password}
              width="true"
            />
            {edit ? (
              <CButton
                onClick={refetchGeneratedPassword}
                type="primary"
                xstyle={{
                  borderRadius: '12px',
                  margin: '16px 0px 0px 16px'
                }}
              >
                Generate
              </CButton>
            ) : null}
          </div>
          <DefaultSelect
            onSelect={(value) => setRole(value)}
            selectStyles={{
              marginTop: '16px'
            }}
            value={role}
          >
            <MenuItem value="">
              <em>Role</em>
            </MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user">User</MenuItem>
            {currentProject?.name === 'Claim Management' ? (
              <MenuItem value="lawyer">Lawyer</MenuItem>
            ) : null}
          </DefaultSelect>
          <div style={{ width: '100%' }}>
            <Row align="middle">
              <Col
                span={24}
                style={{
                  alignItems: 'flex-start',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  justifyContent: 'flex-start'
                }}
              >
                <div className={classes.tagsItem}>
                  {tagsDraft &&
                    Object.entries(tagsDraft)?.map((tagEntry, index) => {
                      return (
                        <div key={index} className={classes.tagItemWrapper}>
                          <CStandardInput
                            allowClear
                            disabled
                            disableUnderline
                            formclasses={{ root: classes.root }}
                            htmlFor="standard-input-customer-tags-object-key"
                            id="standard-input-customer-tags-object-key"
                            label="tag key"
                            size="large"
                            value={tagEntry[0]}
                            width="true"
                          />
                          <CStandardInput
                            allowClear
                            disableUnderline
                            formclasses={{ root: classes.root }}
                            htmlFor="standard-input-customer-tags-object-value"
                            id="standard-input-customer-tags-object-value"
                            label="tag value"
                            onChange={(e) => onObjectValueChange(e.target.value, tagEntry[0])}
                            size="large"
                            value={tagEntry[1]}
                            width="true"
                          />

                          <button
                            className={classes.removeButton}
                            onClick={() => removeKey(tagEntry[0])}
                            type="button"
                          >
                            x
                          </button>
                        </div>
                      );
                    })}
                </div>
                <div className={classes.tagItemWrapperAdd}>
                  <CStandardInput
                    allowClear
                    disableUnderline
                    formclasses={{ root: classes.root }}
                    htmlFor="standard-input-customer-tags-object-key"
                    id="standard-input-customer-tags-object-key"
                    label="tag key"
                    onChange={(e) => setNewTagKey(e.target.value)}
                    size="large"
                    value={newTagKey}
                    width="true"
                  />
                  <CButton
                    onClick={onObjectKeyAdd}
                    size="large"
                    type="secondary"
                    xstyle={{ marginTop: '12px' }}
                  >
                    Add
                  </CButton>
                </div>
              </Col>
            </Row>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Users;
